import React, { useState, useEffect } from 'react';
import { Users, Download, Trash2, RefreshCw, Search, Plus, User, Edit, X, Check } from 'lucide-react';
import { getGuestList, addGuest, updateGuest, deleteGuest } from '../lib/tables';
import GuestListUploader from './GuestListUploader';
import * as XLSX from 'xlsx';
import type { GuestListItem, GuestListUpload } from '../types/tables';

interface Props {
  registryId: string;
}

export default function GuestListManager({ registryId }: Props) {
  const [guests, setGuests] = useState<GuestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<GuestListItem | null>(null);
  const [newGuest, setNewGuest] = useState<GuestListUpload>({
    guest_name: ''
  });

  useEffect(() => {
    loadGuests();
  }, [registryId]);

  const loadGuests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getGuestList(registryId);
      setGuests(data);
    } catch (err) {
      console.error('Error loading guest list:', err);
      setError('Failed to load guest list');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Format data for export
    const exportData = guests.map(guest => ({
      'Guest Name': guest.guest_name,
      'Contact Info': guest.contact_info || '',
      'Dietary Restrictions': guest.dietary_restrictions || '',
      'Plus One Status': guest.plus_one_status ? 'Yes' : 'No',
      'Plus One Name': guest.plus_one_name || ''
    }));
    
    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest List');
    
    // Generate Excel file
    XLSX.writeFile(workbook, 'guest_list.xlsx');
  };

  const handleAddGuest = async () => {
    if (!newGuest.guest_name.trim()) {
      setError('Guest name is required');
      return;
    }

    const result = await addGuest(registryId, newGuest);
    if (result.success) {
      setNewGuest({
        guest_name: ''
      });
      setShowAddForm(false);
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const handleEditGuest = async () => {
    if (!editingGuest) return;

    const result = await updateGuest(editingGuest.id, {
      guest_name: editingGuest.guest_name,
      contact_info: editingGuest.contact_info,
      dietary_restrictions: editingGuest.dietary_restrictions,
      plus_one_status: editingGuest.plus_one_status,
      plus_one_name: editingGuest.plus_one_name
    });

    if (result.success) {
      setEditingGuest(null);
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const handleDeleteGuest = async (guestId: string, guestName: string) => {
    if (!confirm(`Are you sure you want to remove ${guestName} from the guest list?`)) {
      return;
    }

    const result = await deleteGuest(guestId);
    if (result.success) {
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const filteredGuests = guests.filter(guest => 
    guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (guest.contact_info && guest.contact_info.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-pastel-purple-600" />
          <h3 className="text-lg font-medium text-gray-800 font-poppins">Guest List Management</h3>
        </div>
        <div className="flex items-center space-x-2">
          {guests.length > 0 && (
            <button
              type="button"
              onClick={handleExport}
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-white/60 rounded-md hover:bg-white/80 text-gray-600 hover:text-gray-800"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          )}
          <button
            type="button"
            onClick={loadGuests}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-white/60 rounded-md hover:bg-white/80 text-gray-600 hover:text-gray-800"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-pastel-green-400 rounded-md hover:bg-pastel-green-500 text-white"
          >
            <Plus className="w-4 h-4" />
            <span>Add Guest</span>
          </button>
          <button
            type="button"
            onClick={() => setShowUploader(!showUploader)}
            className="flex items-center space-x-1 px-3 py-1 text-sm btn-pastel rounded-md text-white"
          >
            {showUploader ? (
              <>
                <X className="w-4 h-4" />
                <span>Cancel Upload</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Upload List</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
          {error}
        </div>
      )}

      {/* Add Guest Form */}
      {showAddForm && (
        <div className="card-pastel p-4 rounded-lg space-y-4">
          <h4 className="text-gray-800 font-medium font-poppins">Add New Guest</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Guest Name *
              </label>
              <input
                type="text"
                value={newGuest.guest_name}
                onChange={(e) => setNewGuest({ ...newGuest, guest_name: e.target.value })}
                className="input-pastel"
                placeholder="Enter guest name"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewGuest({
                  guest_name: ''
                });
                setError('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddGuest}
              className="px-4 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500"
            >
              Add Guest
            </button>
          </div>
        </div>
      )}

      {showUploader ? (
        <GuestListUploader 
          registryId={registryId} 
          onUploadSuccess={() => {
            loadGuests();
            setShowUploader(false);
          }} 
        />
      ) : (
        <>
          {guests.length > 0 ? (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 input-pastel"
                  placeholder="Search guests..."
                />
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredGuests.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredGuests.map((guest) => (
                    <div key={guest.id} className="card-pastel p-4 rounded-lg">
                      {editingGuest?.id === guest.id ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Guest Name *
                              </label>
                              <input
                                type="text"
                                value={editingGuest.guest_name}
                                onChange={(e) => setEditingGuest({ ...editingGuest, guest_name: e.target.value })}
                                className="input-pastel"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Contact Info
                              </label>
                              <input
                                type="text"
                                value={editingGuest.contact_info || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, contact_info: e.target.value })}
                                className="input-pastel"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Dietary Restrictions
                              </label>
                              <input
                                type="text"
                                value={editingGuest.dietary_restrictions || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, dietary_restrictions: e.target.value })}
                                className="input-pastel"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-600 mb-1">
                                Plus One Name
                              </label>
                              <input
                                type="text"
                                value={editingGuest.plus_one_name || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, plus_one_name: e.target.value })}
                                className="input-pastel"
                              />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`edit-plus-one-${guest.id}`}
                              checked={editingGuest.plus_one_status}
                              onChange={(e) => setEditingGuest({ ...editingGuest, plus_one_status: e.target.checked })}
                              className="h-4 w-4 bg-white/60 border-white/40 rounded text-primary focus:ring-primary"
                            />
                            <label htmlFor={`edit-plus-one-${guest.id}`} className="text-sm text-gray-600">
                              Has Plus One
                            </label>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => setEditingGuest(null)}
                              className="px-3 py-1 text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleEditGuest}
                              className="px-3 py-1 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              <div>
                                <h4 className="font-medium text-gray-800">{guest.guest_name}</h4>
                                <div className="text-sm text-gray-600 space-y-1">
                                  {guest.contact_info && (
                                    <p>Contact: {guest.contact_info}</p>
                                  )}
                                  {guest.dietary_restrictions && (
                                    <p>Dietary: {guest.dietary_restrictions}</p>
                                  )}
                                  {guest.plus_one_status && (
                                    <p>Plus One: {guest.plus_one_name || 'Yes'}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => setEditingGuest(guest)}
                              className="text-pastel-blue-600 hover:text-pastel-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteGuest(guest.id, guest.guest_name)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 card-pastel rounded-lg">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No guests found matching your search</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 card-pastel rounded-lg">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-800 mb-2 font-poppins">No Guests Yet</h4>
              <p className="text-gray-500 mb-4">Upload a guest list or add guests manually</p>
              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500"
                >
                  Add First Guest
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploader(true)}
                  className="px-4 py-2 btn-pastel text-white rounded-md"
                >
                  Upload Guest List
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
